import { useTranslate } from "@tolgee/react";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { relocatePublishButtons } from "../../lomavisCreator/utils/uploadMediaFile";
import { SUGGESTAPOSTVIEW } from "../utils/constant";
import { useDispatch } from "react-redux";
import { updateUserInformation } from "../../../redux/slices/externalContent/externalContent";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import CollectHero from "./collectHero";
import {
  clearRememberedContributor,
  readRememberedContributor,
  writeRememberedContributor,
} from "../utils/rememberedContributor";

interface Props {
  setCurrentView: React.Dispatch<React.SetStateAction<string>>;
  currentView: string;
}

const PersonalInformation: React.FC<Props> = ({
  setCurrentView,
  currentView,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const footerRef = useRef(null);
  const [rightMargin, setRightMargin] = useState(0);

  // Read remembered contributor info once on mount. If present, render the
  // Welcome-Back card first; users can hit "Continue" to skip the form or
  // "Not you?" to clear it and re-enter.
  const [remembered, setRemembered] = useState(() =>
    readRememberedContributor()
  );
  const [showFormDespiteRemembered, setShowFormDespiteRemembered] =
    useState(false);

  const [formData, setFormData] = useState({
    name: remembered?.name || "",
    email: remembered?.email || "",
    message: "",
    licenceInfo: remembered?.licenceInfo || "",
    policyCheck: false,
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    policyCheck: "",
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {
      name: "",
      email: "",
      policyCheck: "",
    };
    if (formData.name.length < 2) {
      errors.name = t("externalContent.validation.name");
    }
    if (!validateEmail(formData.email)) {
      errors.email = t("externalContent.validation.email");
    }
    if (!formData.policyCheck) {
      errors.policyCheck = t("externalContent.validation.policyCheck");
    }
    setFormErrors(errors);
    return !errors.name && !errors.email && !errors.policyCheck;
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [id]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = () => {
    if (validateForm()) {
      dispatch(updateUserInformation({ formData }));
      if (rememberMe) {
        writeRememberedContributor({
          name: formData.name,
          email: formData.email,
          licenceInfo: formData.licenceInfo,
        });
      } else {
        // User unchecked: don't leave stale data on the device.
        clearRememberedContributor();
      }
      setCurrentView(SUGGESTAPOSTVIEW);
    } else {
      toast.error(`${t("externalConternt.errors.missingFormData")}`, {
        duration: 4000,
        position: "top-right",
      });
    }
  };

  const handleContinueAsRemembered = () => {
    if (!remembered) return;
    dispatch(
      updateUserInformation({
        formData: {
          name: remembered.name,
          email: remembered.email,
          message: "",
          licenceInfo: remembered.licenceInfo,
          policyCheck: true,
        },
      })
    );
    // Refresh the TTL window — they're an active contributor. Carry the
    // original ToS-acceptance timestamp forward so we don't claim a fresh
    // acceptance happened when only the contributor's identity was reused.
    writeRememberedContributor({
      name: remembered.name,
      email: remembered.email,
      licenceInfo: remembered.licenceInfo,
      toSAcceptedAt: remembered.toSAcceptedAt,
    });
    setCurrentView(SUGGESTAPOSTVIEW);
  };

  const handleForgetRemembered = () => {
    clearRememberedContributor();
    setRemembered(null);
    setShowFormDespiteRemembered(true);
    setFormData({
      name: "",
      email: "",
      message: "",
      licenceInfo: "",
      policyCheck: false,
    });
  };

  useEffect(() => {
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin,
    });
  }, []);
  function cleanUrl(url) {
    return url.replace(/[\u200B-\u200D\uFEFF]/g, "");
  }

  if (remembered && !showFormDespiteRemembered) {
    return (
      <div
        className="w-100 d-flex flex-column gap-4 bg-white rounded-4 mx-auto p-4 p-md-5"
        style={{ maxWidth: 900 }}
      >
        <CollectHero step={1} stepLabelKey="externalContent.step.about" />
        <div className="d-flex flex-column gap-3">
          <div>
            <h3 className="mb-1">
              {t("externalContent.welcomeBack.title")}
              {remembered.name ? `, ${remembered.name.split(" ")[0]}` : ""}
            </h3>
            <p className="text-muted m-0">
              {t("externalContent.welcomeBack.subtitle")}
            </p>
          </div>
          <div
            className="welcome-back-identity d-flex flex-row align-items-center gap-3 p-3 rounded bg-light"
            style={{
              border: "1px solid rgba(15, 23, 42, 0.06)",
              cursor: "pointer",
              transition:
                "background-color 150ms ease-out, border-color 150ms ease-out, box-shadow 150ms ease-out",
            }}
            role="button"
            tabIndex={0}
            aria-label={t("externalContent.welcomeBack.continueAs", {
              name: remembered.name.split(" ")[0] || remembered.name,
            })}
            onClick={handleContinueAsRemembered}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleContinueAsRemembered();
              }
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.backgroundColor = "#eef5ff";
              el.style.borderColor = "rgba(54, 153, 255, 0.35)";
              el.style.boxShadow = "0 2px 8px rgba(54, 153, 255, 0.12)";
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.style.backgroundColor = "";
              el.style.borderColor = "rgba(15, 23, 42, 0.06)";
              el.style.boxShadow = "";
            }}
          >
            <i
              className="fa-duotone fa-user text-primary flex-shrink-0"
              style={{ fontSize: "1.5rem" }}
            ></i>
            <div className="d-flex flex-column flex-grow-1 text-truncate">
              <span className="fw-semibold text-truncate">
                {remembered.name}
              </span>
              <span
                className="text-muted small text-truncate"
                style={{ fontSize: "0.85rem" }}
              >
                {remembered.email}
              </span>
            </div>
            <i
              className="fa-regular fa-arrow-right text-primary flex-shrink-0"
              aria-hidden="true"
            ></i>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2 mt-2">
            <Button
              variant="primary"
              size="lg"
              onClick={handleContinueAsRemembered}
              className="flex-grow-1"
            >
              {t("externalContent.welcomeBack.continueAs", {
                name: remembered.name.split(" ")[0] || remembered.name,
              })}
              <i className="fa-regular fa-arrow-right ms-2"></i>
            </Button>
          </div>
          <button
            type="button"
            className="btn btn-link btn-sm text-muted align-self-center mt-1 p-0"
            onClick={handleForgetRemembered}
            style={{ minHeight: 32 }}
          >
            {t("externalContent.welcomeBack.notYou")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-100 d-flex flex-column gap-4 bg-white rounded-4 mx-auto p-4 p-md-5"
      style={{ maxWidth: 900 }}
    >
      <CollectHero step={1} stepLabelKey="externalContent.step.about" />
      <h3 className="mt-2"> {t("externalContent.title")}</h3>
      <div className="w-100 d-flex flex-column gap-2 border-top border-bottom py-3">
        <div className="w-100 d-flex flex-column flex-md-row">
          <Form className="w-100 d-flex w-100 gap-1 flex-column flex-sm-row">
            <Form.Group className="mb-3 w-100" controlId="name">
              <Form.Label>{t("externalContent.name")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("externalContent.name.placeholder")}
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!formErrors.name}
                onFocus={() => {
                  setFormErrors((prevState) => ({ ...prevState, name: "" }));
                }}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 w-100" controlId="email">
              <Form.Label>{t("externalContent.email")}</Form.Label>
              <Form.Control
                type="email"
                placeholder={t("externalContent.email.placeholder")}
                value={formData.email}
                onChange={handleChange}
                onFocus={() => {
                  setFormErrors((prevState) => ({ ...prevState, email: "" }));
                }}
                isInvalid={!!formErrors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </div>

        <div className="w-100 d-flex">
          <Form className="w-100 d-flex w-100 gap-1 flex-column flex-sm-row">
            <Form.Group className="mb-3 w-100" controlId="message">
              <Form.Label>{t("externalContent.message")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.message}
                onChange={handleChange}
              />
              <div className="text-muted">
                {t("externalContent.message.subtext")}
              </div>
            </Form.Group>

            <Form.Group className="mb-3 w-100" controlId="licenceInfo">
              <Form.Label>{t("externalContent.licenseInfo")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.licenceInfo}
                onChange={handleChange}
              />
              <div className="text-muted">
                {t("externalContent.licenseInfo.subtext")}
              </div>
            </Form.Group>
          </Form>
        </div>

        <div className="w-100 d-flex flex-column gap-3 mt-2 p-3 rounded bg-light">
          <div className="text-muted small">
            {t("externalContent.confirmationText")}
          </div>
          <Form.Check
            className="w-100 d-flex align-items-start gap-2"
            id="policyCheck"
            style={{ paddingLeft: 0, minHeight: 44 }}
          >
            <Form.Check.Input
              type="checkbox"
              checked={formData.policyCheck}
              onChange={handleChange}
              isInvalid={!!formErrors.policyCheck}
              onFocus={() => {
                setFormErrors((prevState) => ({
                  ...prevState,
                  policyCheck: "",
                }));
              }}
              style={{
                width: 22,
                height: 22,
                marginTop: 2,
                marginLeft: 0,
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
            <Form.Check.Label
              className="ms-2"
              style={{ cursor: "pointer", lineHeight: 1.4 }}
            >
              {t("externalContent.policyCheck.label")}
            </Form.Check.Label>
            <Form.Control.Feedback type="invalid">
              {formErrors.policyCheck}
            </Form.Control.Feedback>
          </Form.Check>
          <div className="d-flex flex-row flex-wrap gap-3 small">
            <a
              href={cleanUrl(t("termsOfServiceUrl"))}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary cursor-pointer"
              style={{ minHeight: 44, display: "inline-flex", alignItems: "center" }}
            >
              {t("externalContent.termsOfService")}
            </a>
            <a
              href={cleanUrl(t("privacyPolicyUrl"))}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary cursor-pointer"
              style={{ minHeight: 44, display: "inline-flex", alignItems: "center" }}
            >
              {t("externalContent.privacyPolicy")}
            </a>
          </div>
        </div>
      </div>

      <Form.Group className="d-flex flex-column gap-1">
        <Form.Check
          className="d-flex align-items-start gap-2"
          id="rememberMeCheck"
          style={{ paddingLeft: 0, minHeight: 44 }}
        >
          <Form.Check.Input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{
              width: 22,
              height: 22,
              marginTop: 2,
              marginLeft: 0,
              flexShrink: 0,
              cursor: "pointer",
            }}
          />
          <Form.Check.Label
            className="ms-2 small"
            style={{ cursor: "pointer", lineHeight: 1.4 }}
          >
            {t("externalContent.rememberMe.label")}
          </Form.Check.Label>
        </Form.Check>
        <div className="text-muted ms-4 ps-1" style={{ fontSize: "0.78rem" }}>
          {t("externalContent.rememberMe.hint")}
        </div>
      </Form.Group>

      <div ref={footerRef} className="w-100 d-flex flex-end">
        <Button
          // style={{ marginRight: rightMargin + "px" }}
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          className="w-md-auto w-100"
        >
          {t("externalContent.next")}
        </Button>
      </div>
    </div>
  );
};

export default PersonalInformation;
